import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Select,
  SelectItem,
  Divider,
  Progress,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  RadioGroup,
  Radio,
  Chip,
  Spinner,
  Input,
} from '@nextui-org/react';
import { toast } from 'react-toastify';
import { Icon } from '@iconify/react';
import * as XLSX from 'xlsx';
import {
  Settlement,
  SettlementDetailRevenue,
  Species,
} from 'common/src/utils/types';
import { supabase } from 'common/src/supabase';
import useUserStore from '../../../../common/src/stores/useUserStore';
import useFilterStore from '../../../../common/src/stores/useFiltersStore';
import useDataStore from '../../../../common/src/stores/useDataStore';
import { SEASONS } from 'common/src/utils/definitions';
import { getSettlementsByExporterId } from 'common/src/api/settlements';
import {
  deleteRevenuesBySettlementAndType,
  bulkCreateSettlementDetailRevenues,
} from 'common/src/api/settlement-detail-revenues';
import { useTranslation } from '../../../../common/src/hooks/useTranslation';

import { REVENUE_TYPES } from '../../utils/revenue-types';

type UploadMode = 'append' | 'replace-matching' | 'replace-all';
type CurrencyMode = 'both' | 'usd' | 'local';
type TcMode = 'per-settlement' | 'fixed';

interface ExcelRow {
  reference_number: string;
  amount_local: number;
  amount_usd: number;
  description: string;
}

interface ValidationError {
  row: number;
  message: string;
}

interface ProcessedRow extends ExcelRow {
  rowIndex: number;
  settlement?: Settlement;
}

interface MassiveRevenueUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const MassiveRevenueUploadModal: React.FC<MassiveRevenueUploadModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation('settlements');
  const { userProfile } = useUserStore();
  const { selectedSeason: globalSeason } = useFilterStore();
  const { species, selectedSpecies: selectedSpeciesFromStore } = useDataStore();

  // Season & species local state (initialized from global)
  const [season, setSeason] = useState<string>(globalSeason || '');
  const [selectedSpecies, setSelectedSpecies] = useState<Species | null>(
    selectedSpeciesFromStore ?? null
  );

  // Settlements fetched for the chosen season/species
  const [settlements, setSettlements] = useState<Settlement[]>([]);
  const [loadingSettlements, setLoadingSettlements] = useState(false);

  // Step: 'config' -> 'review' -> 'uploading'
  const [step, setStep] = useState<'config' | 'review' | 'uploading'>('config');
  const [revenueType, setRevenueType] = useState<string>('');
  const [distribution, setDistribution] = useState<'byKg' | 'bySales'>(
    'bySales'
  );
  const [uploadMode, setUploadMode] = useState<UploadMode>('append');
  const [currencyMode, setCurrencyMode] = useState<CurrencyMode>('both');
  const [tcMode, setTcMode] = useState<TcMode>('per-settlement');
  const [fixedExchangeRate, setFixedExchangeRate] = useState<string>('');
  const [file, setFile] = useState<File | null>(null);
  const [processedRows, setProcessedRows] = useState<ProcessedRow[]>([]);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>(
    []
  );
  const [progress, setProgress] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);

  // Sync global filters into local state on open
  useEffect(() => {
    if (isOpen) {
      if (!season && globalSeason) setSeason(globalSeason);
      if (!selectedSpecies && selectedSpeciesFromStore)
        setSelectedSpecies(selectedSpeciesFromStore);
    }
  }, [isOpen, globalSeason, selectedSpeciesFromStore]);

  // Fetch settlements when season/species change
  useEffect(() => {
    if (!isOpen || !season || !selectedSpecies || !userProfile?.exporter_id)
      return;

    let cancelled = false;
    const fetchData = async () => {
      setLoadingSettlements(true);
      try {
        const data = await getSettlementsByExporterId(
          userProfile.exporter_id,
          undefined,
          season,
          selectedSpecies.id
        );
        if (!cancelled) setSettlements(data || []);
      } catch (error) {
        console.error('Error fetching settlements:', error);
        if (!cancelled) setSettlements([]);
      } finally {
        if (!cancelled) setLoadingSettlements(false);
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [isOpen, season, selectedSpecies, userProfile?.exporter_id]);

  const settlementsByRef = useMemo(() => {
    const map = new Map<string, Settlement>();
    settlements.forEach((s) => {
      if (s.reference_number) {
        map.set(s.reference_number.trim().toUpperCase(), s);
      }
    });
    return map;
  }, [settlements]);

  const resetState = useCallback(() => {
    setStep('config');
    setRevenueType('');
    setDistribution('byKg');
    setUploadMode('append');
    setCurrencyMode('both');
    setTcMode('per-settlement');
    setFixedExchangeRate('');
    setFile(null);
    setProcessedRows([]);
    setValidationErrors([]);
    setProgress(0);
    setIsProcessing(false);
    setSeason(globalSeason || '');
    setSelectedSpecies(selectedSpeciesFromStore ?? null);
    setSettlements([]);
  }, [globalSeason, selectedSpeciesFromStore]);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  const handleDownloadTemplate = useCallback(() => {
    let headers: string[];
    let exampleRow: (string | number)[];
    let colWidths: { wch: number }[];

    if (currencyMode === 'usd') {
      headers = ['Número de Referencia', 'Valor USD', 'Descripción'];
      exampleRow = ['REF-001', 50, 'Ejemplo de descripción'];
      colWidths = [{ wch: 22 }, { wch: 15 }, { wch: 30 }];
    } else if (currencyMode === 'local') {
      headers = ['Número de Referencia', 'Valor Moneda Local', 'Descripción'];
      exampleRow = ['REF-001', 1000, 'Ejemplo de descripción'];
      colWidths = [{ wch: 22 }, { wch: 20 }, { wch: 30 }];
    } else {
      headers = [
        'Número de Referencia',
        'Valor Moneda Local',
        'Valor USD',
        'Descripción',
      ];
      exampleRow = ['REF-001', 1000, 50, 'Ejemplo de descripción'];
      colWidths = [{ wch: 22 }, { wch: 20 }, { wch: 15 }, { wch: 30 }];
    }

    const wsData = [headers, exampleRow];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws['!cols'] = colWidths;
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Otros Ingresos');
    XLSX.writeFile(wb, 'plantilla_otros_ingresos.xlsx');
  }, [currencyMode]);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFile = e.target.files?.[0] || null;
      setFile(selectedFile);
    },
    []
  );

  const getExchangeRate = useCallback(
    (settlement: Settlement): number | null => {
      if (tcMode === 'fixed') {
        const rate = Number(fixedExchangeRate);
        return rate > 0 ? rate : null;
      }
      // per-settlement: use the settlement's exchange_rate
      const rate = settlement.exchange_rate;
      return rate && rate > 0 ? rate : null;
    },
    [tcMode, fixedExchangeRate]
  );

  const processFile = useCallback(async () => {
    if (!file) return;
    setIsProcessing(true);

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet);

      const errors: ValidationError[] = [];
      const rows: ProcessedRow[] = [];

      jsonData.forEach((row, idx) => {
        const rowIndex = idx + 2; // Excel row (1-indexed + header)

        const rawRef =
          row['Número de Referencia'] ?? row['Numero de Referencia'] ?? '';
        const reference_number = String(rawRef).trim();
        if (!reference_number) {
          errors.push({
            row: rowIndex,
            message: t('Falta Número de Referencia'),
          });
          return;
        }

        const description = String(
          row['Descripción'] ?? row['Descripcion'] ?? ''
        ).trim();

        const settlement = settlementsByRef.get(reference_number.toUpperCase());
        if (!settlement) {
          errors.push({
            row: rowIndex,
            message: `${reference_number}: ${t('Liquidación no encontrada')}`,
          });
          return;
        }

        if (
          !settlement.settlement_details ||
          settlement.settlement_details.length === 0
        ) {
          errors.push({
            row: rowIndex,
            message: `${reference_number}: ${t('Liquidación sin detalles')}`,
          });
          return;
        }

        let amount_local = 0;
        let amount_usd = 0;

        if (currencyMode === 'both') {
          const rawLocal =
            row['Valor Moneda Local'] ?? row['Valor moneda local'] ?? 0;
          amount_local = Number(rawLocal);
          if (isNaN(amount_local) || amount_local === 0) {
            errors.push({
              row: rowIndex,
              message: `${reference_number}: ${t('Valor Moneda Local inválido o cero')}`,
            });
            return;
          }

          const rawUsd = row['Valor USD'] ?? row['Valor usd'] ?? 0;
          amount_usd = Number(rawUsd);
          if (isNaN(amount_usd)) {
            errors.push({
              row: rowIndex,
              message: `${reference_number}: ${t('Valor USD inválido')}`,
            });
            return;
          }
        } else if (currencyMode === 'usd') {
          const rawUsd = row['Valor USD'] ?? row['Valor usd'] ?? 0;
          amount_usd = Number(rawUsd);
          if (isNaN(amount_usd) || amount_usd === 0) {
            errors.push({
              row: rowIndex,
              message: `${reference_number}: ${t('Valor USD inválido o cero')}`,
            });
            return;
          }

          const tc = getExchangeRate(settlement);
          if (!tc) {
            errors.push({
              row: rowIndex,
              message: `${reference_number}: ${t('No se encontró tipo de cambio para convertir a moneda local')}`,
            });
            return;
          }
          amount_local = amount_usd * tc;
        } else {
          // currencyMode === 'local'
          const rawLocal =
            row['Valor Moneda Local'] ?? row['Valor moneda local'] ?? 0;
          amount_local = Number(rawLocal);
          if (isNaN(amount_local) || amount_local === 0) {
            errors.push({
              row: rowIndex,
              message: `${reference_number}: ${t('Valor Moneda Local inválido o cero')}`,
            });
            return;
          }

          const tc = getExchangeRate(settlement);
          if (!tc) {
            errors.push({
              row: rowIndex,
              message: `${reference_number}: ${t('No se encontró tipo de cambio para convertir a USD')}`,
            });
            return;
          }
          amount_usd = amount_local / tc;
        }

        rows.push({
          rowIndex,
          reference_number,
          amount_local,
          amount_usd,
          description,
          settlement,
        });
      });

      setValidationErrors(errors);
      setProcessedRows(rows);
      setStep('review');
    } catch (error) {
      console.error('Error processing file:', error);
      toast.error(t('Error al procesar el archivo'));
    } finally {
      setIsProcessing(false);
    }
  }, [file, settlementsByRef, currencyMode, getExchangeRate, t]);

  const handleUpload = useCallback(async () => {
    if (processedRows.length === 0 || !revenueType) return;
    setStep('uploading');
    setProgress(0);

    try {
      const total = processedRows.length;
      let processed = 0;

      // Group rows by settlement
      const bySettlement = new Map<number, ProcessedRow[]>();
      processedRows.forEach((row) => {
        if (!row.settlement) return;
        const id = row.settlement.id;
        const existing = bySettlement.get(id) || [];
        existing.push(row);
        bySettlement.set(id, existing);
      });

      // If replace mode, delete existing revenues first
      if (uploadMode === 'replace-matching' || uploadMode === 'replace-all') {
        if (uploadMode === 'replace-all') {
          // Delete from ALL settlements of this season/species for this type
          for (const s of settlements) {
            await deleteRevenuesBySettlementAndType(s.id, revenueType);
          }
        } else {
          // Delete only from settlements that appear in the Excel
          for (const [settlementId] of bySettlement) {
            await deleteRevenuesBySettlementAndType(settlementId, revenueType);
          }
        }
      }

      // Process each settlement
      for (const [, rows] of bySettlement) {
        const settlement = rows[0].settlement!;
        const details = settlement.settlement_details || [];
        if (details.length === 0) continue;

        const totalKilos = details.reduce((sum, d) => sum + (d.kilos || 0), 0);
        const totalSales = details.reduce(
          (sum, d) => sum + (d.local_total_price || 0),
          0
        );

        // Each Excel row for this settlement creates a distributed set of revenues
        for (const row of rows) {
          const groupId = crypto.randomUUID();
          const inserts: Omit<SettlementDetailRevenue, 'id' | 'created_at'>[] =
            [];

          for (const detail of details) {
            const proportion =
              distribution === 'byKg'
                ? totalKilos > 0
                  ? (detail.kilos || 0) / totalKilos
                  : 0
                : totalSales > 0
                  ? (detail.local_total_price || 0) / totalSales
                  : 0;

            inserts.push({
              settlement_detail_id: detail.id,
              revenue_type: revenueType,
              description: row.description || undefined,
              amount_local: row.amount_local * proportion,
              amount_usd: row.amount_usd * proportion,
              group_id: groupId,
              distribution,
              created_by: userProfile?.id,
            });
          }

          await bulkCreateSettlementDetailRevenues(inserts);
          processed += 1;
          setProgress(Math.round((processed / total) * 100));
        }

        // Update settlement total_other_revenue_local
        const { data: allRevenues } = await supabase
          .from('settlement_detail_revenues')
          .select('amount_local, settlements_details!inner(settlement_id)')
          .eq('settlements_details.settlement_id', settlement.id);

        const newTotal = (allRevenues || []).reduce(
          (sum: number, r: { amount_local: number }) =>
            sum + (r.amount_local || 0),
          0
        );

        await supabase
          .from('settlements')
          .update({ total_other_revenue_local: newTotal })
          .eq('id', settlement.id);
      }

      toast.success(
        `${t('Se procesaron')} ${processed} ${t('filas exitosamente')}`
      );
      onSuccess();
      handleClose();
    } catch (error) {
      console.error('Error uploading revenues:', error);
      toast.error(t('Error al subir los ingresos'));
      setStep('review');
    }
  }, [
    processedRows,
    revenueType,
    distribution,
    uploadMode,
    settlements,
    userProfile?.id,
    onSuccess,
    handleClose,
    t,
  ]);

  const revenueTypeLabel = useMemo(() => {
    return REVENUE_TYPES.find((rt) => rt.value === revenueType)?.label || '';
  }, [revenueType]);

  const canProcess =
    file &&
    revenueType &&
    season &&
    selectedSpecies &&
    !loadingSettlements &&
    settlements.length > 0 &&
    (currencyMode === 'both' ||
      tcMode === 'per-settlement' ||
      (tcMode === 'fixed' && Number(fixedExchangeRate) > 0));

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size='3xl'
      scrollBehavior='inside'
    >
      <ModalContent>
        <ModalHeader className='flex flex-col gap-1'>
          <span>
            {t('Subida Masiva')} - {t('Otros Ingresos')}
          </span>
          {step !== 'config' && (
            <span className='text-sm text-default-500 font-normal'>
              {selectedSpecies?.name} | {season} | {revenueTypeLabel} |{' '}
              {distribution === 'byKg' ? t('Por Kg') : t('Por Venta')}
              {currencyMode !== 'both' && (
                <> | {currencyMode === 'usd' ? 'USD' : t('Moneda Local')} → {tcMode === 'fixed' ? `TC ${fixedExchangeRate}` : t('TC por liquidación')}</>
              )}
            </span>
          )}
        </ModalHeader>
        <ModalBody>
          {step === 'config' && (
            <div className='flex flex-col gap-4'>
              <div className='grid grid-cols-2 gap-4'>
                <Select
                  label={t('Temporada') + ' *'}
                  selectedKeys={season ? [season] : []}
                  onChange={(e) => setSeason(e.target.value)}
                  size='sm'
                >
                  {Object.values(SEASONS).map((s) => (
                    <SelectItem key={s.name} value={s.name}>
                      {s.name}
                    </SelectItem>
                  ))}
                </Select>

                <Select
                  label={t('Especie') + ' *'}
                  selectedKeys={
                    selectedSpecies ? [selectedSpecies.id.toString()] : []
                  }
                  onChange={(e) => {
                    const sp = species.find(
                      (sp) => sp.id.toString() === e.target.value
                    );
                    setSelectedSpecies(sp || null);
                  }}
                  size='sm'
                >
                  {species.map((sp) => (
                    <SelectItem key={sp.id} value={sp.id.toString()}>
                      {sp.name}
                    </SelectItem>
                  ))}
                </Select>
              </div>

              {loadingSettlements && (
                <div className='flex items-center gap-2 text-default-500 text-sm'>
                  <Spinner size='sm' />
                  {t('Cargando liquidaciones...')}
                </div>
              )}

              {!loadingSettlements && season && selectedSpecies && (
                <Chip
                  size='sm'
                  variant='flat'
                  color={settlements.length > 0 ? 'success' : 'warning'}
                >
                  {settlements.length} {t('liquidaciones encontradas')}
                </Chip>
              )}

              <Divider />

              <Select
                label={t('Tipo de Ingreso')}
                placeholder={t('Selecciona el tipo')}
                selectedKeys={revenueType ? [revenueType] : []}
                onSelectionChange={(keys) => {
                  const val = Array.from(keys)[0] as string;
                  setRevenueType(val || '');
                  // Set default distribution based on revenue type
                  const byKgTypes = ['rebate', 'other_costs_adjustment', 'freight_adjustment'];
                  setDistribution(byKgTypes.includes(val) ? 'byKg' : 'bySales');
                }}
              >
                {REVENUE_TYPES.map((rt) => (
                  <SelectItem key={rt.value} value={rt.value}>
                    {rt.label}
                  </SelectItem>
                ))}
              </Select>

              <RadioGroup
                label={t('Algoritmo de Distribución')}
                value={distribution}
                onValueChange={(val) => {
                  if (val === 'byKg' || val === 'bySales') setDistribution(val);
                }}
              >
                <Radio
                  value='byKg'
                  description={t(
                    'El monto se reparte entre los detalles de la liquidación en proporción a sus kilos. Ideal para costos logísticos como flete, rebate por kg, ajuste de otros costos.'
                  )}
                >
                  {t('Por Kg')}
                </Radio>
                <Radio
                  value='bySales'
                  description={t(
                    'El monto se reparte entre los detalles de la liquidación en proporción a su valor de venta local. Ideal para compensaciones, ajustes de ingreso, comisión, impuestos y comparativas.'
                  )}
                >
                  {t('Por Venta')}
                </Radio>
              </RadioGroup>

              <Divider />

              <RadioGroup
                label={t('Valores a subir')}
                value={currencyMode}
                onValueChange={(val) => setCurrencyMode(val as CurrencyMode)}
              >
                <Radio value='both'>
                  {t('Estoy subiendo ambos valores (USD y Moneda Local)')}
                </Radio>
                <Radio value='usd'>
                  {t('Estoy subiendo valores en USD')}
                </Radio>
                <Radio value='local'>
                  {t('Estoy subiendo valores en Moneda Local')}
                </Radio>
              </RadioGroup>

              {currencyMode !== 'both' && (
                <div className='flex flex-col gap-3 ml-4 p-3 bg-default-50 rounded-lg'>
                  <RadioGroup
                    label={t('Tipo de Cambio')}
                    value={tcMode}
                    onValueChange={(val) => setTcMode(val as TcMode)}
                    size='sm'
                  >
                    <Radio value='per-settlement'>
                      {t('Usar TC de cada liquidación')}
                    </Radio>
                    <Radio value='fixed'>
                      {t('Establecer TC para todas las liquidaciones')}
                    </Radio>
                  </RadioGroup>
                  {tcMode === 'fixed' && (
                    <Input
                      label={t('Tipo de Cambio')}
                      placeholder='850'
                      type='number'
                      size='sm'
                      value={fixedExchangeRate}
                      onValueChange={setFixedExchangeRate}
                      description={
                        currencyMode === 'usd'
                          ? t('Se multiplicará USD × TC para obtener Moneda Local')
                          : t('Se dividirá Moneda Local ÷ TC para obtener USD')
                      }
                    />
                  )}
                </div>
              )}

              <Divider />

              <RadioGroup
                label={t('Modo de Carga')}
                value={uploadMode}
                onValueChange={(val) => setUploadMode(val as UploadMode)}
              >
                <Radio value='append'>{t('Agregar estos ingresos')}</Radio>
                <Radio value='replace-matching'>
                  {t(
                    'Borrar ingresos del tipo seleccionado en las liquidaciones del Excel y resubir'
                  )}
                </Radio>
                <Radio value='replace-all'>
                  {t(
                    'Borrar TODOS los ingresos del tipo seleccionado en TODAS las liquidaciones de la temporada y especie seleccionada y resubir'
                  )}
                </Radio>
              </RadioGroup>

              <Divider />

              <div className='flex flex-col gap-2'>
                <Button
                  variant='flat'
                  startContent={<Icon icon='tabler:download' />}
                  onClick={handleDownloadTemplate}
                  size='sm'
                >
                  {t('Descargar Plantilla')}
                </Button>

                <div className='flex items-center gap-2'>
                  <input
                    type='file'
                    accept='.xlsx,.xls,.csv'
                    onChange={handleFileChange}
                    className='block w-full text-sm text-default-500
                      file:mr-4 file:py-2 file:px-4
                      file:rounded-md file:border-0
                      file:text-sm file:font-semibold
                      file:bg-primary-50 file:text-primary-700
                      hover:file:bg-primary-100'
                  />
                </div>
              </div>
            </div>
          )}

          {step === 'review' && (
            <div className='flex flex-col gap-4'>
              <div className='flex gap-2 flex-wrap'>
                <Chip color='success' variant='flat'>
                  {processedRows.length} {t('filas válidas')}
                </Chip>
                {validationErrors.length > 0 && (
                  <Chip color='danger' variant='flat'>
                    {validationErrors.length} {t('errores')}
                  </Chip>
                )}
                {uploadMode !== 'append' && (
                  <Chip color='warning' variant='flat'>
                    {uploadMode === 'replace-all'
                      ? `${t('Se borrarán TODOS los ingresos del tipo en')} ${settlements.length} ${t('liquidaciones de')} ${selectedSpecies?.name} ${season}`
                      : t(
                          'Se borrarán ingresos del tipo en liquidaciones del Excel'
                        )}
                  </Chip>
                )}
              </div>

              {validationErrors.length > 0 && (
                <div className='max-h-40 overflow-y-auto'>
                  <Table aria-label='Validation errors' isCompact removeWrapper>
                    <TableHeader>
                      <TableColumn>{t('Fila')}</TableColumn>
                      <TableColumn>{t('Error')}</TableColumn>
                    </TableHeader>
                    <TableBody>
                      {validationErrors.map((err, i) => (
                        <TableRow key={i}>
                          <TableCell>{err.row}</TableCell>
                          <TableCell className='text-danger'>
                            {err.message}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {processedRows.length > 0 && (
                <>
                  <Divider />
                  <div className='max-h-60 overflow-y-auto'>
                    <Table aria-label='Preview data' isCompact removeWrapper>
                      <TableHeader>
                        <TableColumn>{t('Referencia')}</TableColumn>
                        <TableColumn>{t('Valor Moneda Local')}</TableColumn>
                        <TableColumn>{t('Valor USD')}</TableColumn>
                        <TableColumn>{t('Descripción')}</TableColumn>
                      </TableHeader>
                      <TableBody>
                        {processedRows.map((row, i) => (
                          <TableRow key={i}>
                            <TableCell>{row.reference_number}</TableCell>
                            <TableCell>
                              {row.amount_local.toLocaleString('es-CL', {
                                minimumFractionDigits: 2,
                              })}
                            </TableCell>
                            <TableCell>
                              {row.amount_usd.toLocaleString('en-US', {
                                minimumFractionDigits: 2,
                              })}
                            </TableCell>
                            <TableCell>{row.description}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </>
              )}
            </div>
          )}

          {step === 'uploading' && (
            <div className='flex flex-col gap-4 py-8'>
              <Progress
                aria-label='Upload progress'
                value={progress}
                color='primary'
                showValueLabel
                className='max-w-full'
              />
              <p className='text-center text-default-500'>
                {t('Procesando ingresos...')}
              </p>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          {step === 'config' && (
            <>
              <Button variant='light' onPress={handleClose}>
                {t('Cancelar')}
              </Button>
              <Button
                color='primary'
                onPress={processFile}
                isDisabled={!canProcess}
                isLoading={isProcessing}
                startContent={<Icon icon='tabler:file-spreadsheet' />}
              >
                {t('Procesar Archivo')}
              </Button>
            </>
          )}
          {step === 'review' && (
            <>
              <Button variant='light' onPress={() => setStep('config')}>
                {t('Volver')}
              </Button>
              <Button
                color='primary'
                onPress={handleUpload}
                isDisabled={processedRows.length === 0}
                startContent={<Icon icon='tabler:upload' />}
              >
                {t('Subir')} {processedRows.length} {t('ingresos')}
              </Button>
            </>
          )}
          {step === 'uploading' && (
            <Button variant='flat' isDisabled>
              {t('Procesando...')}
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default MassiveRevenueUploadModal;
